import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { n } from "../../lib/format";
import { akkumuleretVed, formatTid, yTicks, type Projektion } from "./projection";

/* Chart geometry in viewBox units. The SVG scales to its container, so these
   are fixed and the whole thing stays crisp in print. */
const W = 960;
const H = 440;
const PAD = { top: 20, right: 188, bottom: 46, left: 74 };
const PLOT = { w: W - PAD.left - PAD.right, h: H - PAD.top - PAD.bottom };
/** Minimum vertical distance between two end labels before they get pushed apart. */
const LABEL_H = 34;

interface Hover {
  /** Fractional years under the pointer. */
  tid: number;
  /** Pointer position in px, relative to the chart box. */
  x: number;
  y: number;
  /** Chart box size, so the tooltip can be kept inside it. */
  boksW: number;
  boksH: number;
}

/** Tooltip width — the height is never assumed, see tipPos(). */
const TIP_W = 220;

/**
 * Accumulated cost over time — one line per selected agreement.
 *
 * Hand-rolled SVG rather than a charting library: it is ~200 lines, inherits the
 * theme tokens directly (so dark mode and print need no extra work), and vector
 * output is exactly what the print-to-PDF path wants.
 */
export default function CostChart({ p }: { p: Projektion }) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<Hover | null>(null);

  const { ticks, top } = useMemo(() => yTicks(p.maxBelob), [p.maxBelob]);

  const x = useCallback((aar: number) => PAD.left + (aar / p.aar) * PLOT.w, [p.aar]);
  const y = useCallback((kr: number) => PAD.top + PLOT.h - (kr / top) * PLOT.h, [top]);

  /** End labels, pushed apart just enough not to overlap — leader lines keep them attached. */
  const labels = useMemo(() => {
    const raw = p.serier
      .map((s) => ({ serie: s, anker: y(akkumuleretVed(s.forloeb, p.aar)) }))
      .sort((a, b) => a.anker - b.anker);
    let forrige = -Infinity;
    return raw.map((r) => {
      const py = Math.max(r.anker, forrige + LABEL_H);
      forrige = py;
      return { ...r, y: py };
    });
  }, [p.serier, p.aar, y]);

  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    // Years come from the SVG box (it may be scrolled sideways); the tooltip is
    // placed against the outer box, which is what it must stay inside of.
    const svgBox = svgRef.current?.getBoundingClientRect();
    const wrapBox = wrapRef.current?.getBoundingClientRect();
    if (!svgBox || !wrapBox) return;
    // Client px → viewBox units → years, clamped to the plot area.
    const vb = ((e.clientX - svgBox.left) / svgBox.width) * W;
    const tid = Math.min(p.aar, Math.max(0, ((vb - PAD.left) / PLOT.w) * p.aar));
    setHover({
      tid,
      x: e.clientX - wrapBox.left,
      y: e.clientY - wrapBox.top,
      boksW: wrapBox.width,
      boksH: wrapBox.height,
    });
  };

  /**
   * Tooltip placement, kept inside the chart box on both axes.
   *
   * Horizontally we know the width, so we flip to the left of the cursor when
   * the right side won't fit, then clamp. Vertically the height depends on how
   * many series are listed, so instead of guessing it we anchor from the bottom
   * in the lower half — then the box grows upwards, away from the edge, and no
   * height estimate can be wrong.
   */
  const tipPos = (h: Hover): { left: number; top?: number; bottom?: number } => {
    const hoejre = h.x + 16;
    const venstre = h.x - 16 - TIP_W;
    const x = hoejre + TIP_W + 8 <= h.boksW ? hoejre : venstre;
    const left = Math.max(8, Math.min(x, h.boksW - TIP_W - 8));
    return h.y > h.boksH / 2
      ? { left, bottom: Math.max(8, h.boksH - h.y + 20) }
      : { left, top: Math.max(8, h.y + 20) };
  };

  const hoverVaerdier = useMemo(() => {
    if (!hover) return [];
    return p.serier
      .map((s) => ({ serie: s, kr: akkumuleretVed(s.forloeb, hover.tid) }))
      .sort((a, b) => a.kr - b.kr);
  }, [hover, p.serier]);

  return (
    // Two nested elements on purpose: the inner one scrolls, the outer one is the
    // positioning context. An absolutely-positioned tooltip inside a scroller
    // widens its scroll area and makes scrollbars appear as it nears the edges.
    <div
      className="relative"
      ref={wrapRef}
      onPointerMove={onMove}
      onPointerLeave={() => setHover(null)}
    >
      <div className="overflow-x-auto print:overflow-visible">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full min-w-[720px] touch-none select-none print:min-w-0"
        role="img"
        aria-label={t("sam.grafAlt", { aar: p.aar, antal: p.serier.length })}
      >
        {/* Gridlines + y ticks — hairline, one step off the surface, recessive. */}
        {ticks.map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={PAD.left + PLOT.w} y1={y(v)} y2={y(v)} stroke="var(--line)" strokeWidth={1} />
            <text
              x={PAD.left - 12}
              y={y(v) + 4}
              textAnchor="end"
              className="fill-ink-faint font-mono"
              fontSize={13}
            >
              {v === 0 ? "0" : n(v)}
            </text>
          </g>
        ))}

        {/* x axis: whole years across the horizon */}
        {Array.from({ length: p.aar + 1 }, (_, i) => i).map((i) => (
          <text
            key={i}
            x={x(i)}
            y={PAD.top + PLOT.h + 26}
            textAnchor="middle"
            className="fill-ink-faint font-mono"
            fontSize={13}
          >
            {i === 0 ? t("sam.nu") : i}
          </text>
        ))}
        <text
          x={PAD.left + PLOT.w / 2}
          y={H - 6}
          textAnchor="middle"
          className="fill-ink-faint font-mono"
          fontSize={12}
        >
          {t("sam.aarAkse")}
        </text>

        {/* One break-even marker — the moment the eventual winner takes the lead.
            Drawn under the lines so it never hides the data. */}
        {p.noegleVending && (
          <g>
            <line
              x1={x(p.noegleVending.tidAar)}
              x2={x(p.noegleVending.tidAar)}
              y1={y(p.noegleVending.belob)}
              y2={PAD.top + PLOT.h}
              stroke="var(--line-strong)"
              strokeWidth={1}
            />
            <circle
              cx={x(p.noegleVending.tidAar)}
              cy={y(p.noegleVending.belob)}
              r={5.5}
              fill="none"
              stroke="var(--ink-faint)"
              strokeWidth={1.5}
            />
            <text
              x={x(p.noegleVending.tidAar)}
              y={PAD.top + PLOT.h - 10}
              textAnchor="middle"
              className="fill-ink-faint font-mono"
              fontSize={11.5}
            >
              {t("sam.markoer", { tid: formatTid(p.noegleVending.tidAar, t) })}
            </text>
          </g>
        )}

        {p.serier.map((s) => {
          const d = s.forloeb.punkter.map((pt, i) => `${i === 0 ? "M" : "L"}${x(pt.aar)},${y(pt.akkumuleret)}`).join(" ");
          const slut = akkumuleretVed(s.forloeb, p.aar);
          return (
            <g key={s.produktversionId}>
              <path d={d} fill="none" stroke={s.farve} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              {/* Year-0 dot: the establishment cost, visible even when it is zero. */}
              <circle cx={x(0)} cy={y(s.forloeb.etablering)} r={4.5} fill={s.farve} stroke="var(--surface)" strokeWidth={2} />
              <circle cx={x(p.aar)} cy={y(slut)} r={4.5} fill={s.farve} stroke="var(--surface)" strokeWidth={2} />
            </g>
          );
        })}

        {/* Direct end labels in a gutter, with leader lines — never stacked onto the curve. */}
        {labels.map(({ serie, anker, y: ly }) => (
          <g key={serie.produktversionId}>
            <path
              d={`M${x(p.aar) + 7},${anker} L${PAD.left + PLOT.w + 22},${ly}`}
              fill="none"
              stroke="var(--line-strong)"
              strokeWidth={1}
            />
            <rect x={PAD.left + PLOT.w + 26} y={ly - 7} width={9} height={9} rx={2} fill={serie.farve} />
            <text x={PAD.left + PLOT.w + 41} y={ly - 3} className="fill-ink" fontSize={13.5} fontWeight={600}>
              {klip(serie.navn, 20)}
            </text>
            <text x={PAD.left + PLOT.w + 41} y={ly + 13} className="fill-ink-faint font-mono" fontSize={12}>
              {n(akkumuleretVed(serie.forloeb, p.aar))} kr
            </text>
          </g>
        ))}

        {hover && (
          <line
            x1={x(hover.tid)}
            x2={x(hover.tid)}
            y1={PAD.top}
            y2={PAD.top + PLOT.h}
            stroke="var(--ink-faint)"
            strokeWidth={1}
          />
        )}
      </svg>
      </div>

      {hover && (
        <div
          className="pointer-events-none absolute z-10 hidden rounded-m border border-line bg-surface p-3 shadow-lift sm:block print:hidden"
          style={{ width: TIP_W, ...tipPos(hover) }}
        >
          <div className="mb-2 font-mono text-[11px] uppercase tracking-wide text-ink-faint">
            {t("sam.efter")} {formatTid(hover.tid, t)}
          </div>
          {hoverVaerdier.map(({ serie, kr }) => (
            <div key={serie.produktversionId} className="flex items-center gap-2 py-0.5 text-[13px]">
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: serie.farve }} />
              <span className="min-w-0 flex-1 truncate text-ink-soft">{serie.navn}</span>
              <span className="num shrink-0 font-semibold">{n(kr)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Trim a series name so the label gutter never overflows the viewBox. */
function klip(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1).trimEnd()}…`;
}
